import { marked } from "marked"

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
})

interface MarkdownProps {
  content: string
  className?: string
}

/**
 * Shared markdown renderer. Parses markdown with `marked` and renders it with
 * scoped typography styles (headings, lists, tables, code, etc.).
 */
export function Markdown({ content, className = "" }: MarkdownProps) {
  const html = marked.parse(content) as string
  return (
    <>
      <MarkdownStyles />
      <div class={`mw-markdown ${className}`} dangerouslySetInnerHTML={{ __html: html }} />
    </>
  )
}

/** Scoped styles for the shared Markdown component */
function MarkdownStyles() {
  return (
    <style>{`
      .mw-markdown {
        line-height: 1.7;
      }
      .mw-markdown > *:first-child {
        margin-top: 0;
      }
      .mw-markdown > *:last-child {
        margin-bottom: 0;
      }

      /* Headings */
      .mw-markdown h1,
      .mw-markdown h2,
      .mw-markdown h3,
      .mw-markdown h4 {
        font-weight: 600;
        margin-top: 1.25em;
        margin-bottom: 0.5em;
        color: #1f2937;
      }
      .mw-markdown h1 { font-size: 1.4em; }
      .mw-markdown h2 { font-size: 1.2em; }
      .mw-markdown h3 { font-size: 1.05em; }

      /* Paragraphs */
      .mw-markdown p {
        margin-bottom: 0.75em;
      }

      /* Lists */
      .mw-markdown ul {
        list-style-type: disc;
        margin-left: 1.5em;
        margin-bottom: 0.75em;
      }
      .mw-markdown ol {
        list-style-type: decimal;
        margin-left: 1.5em;
        margin-bottom: 0.75em;
      }
      .mw-markdown li {
        margin-bottom: 0.25em;
      }
      .mw-markdown li > p {
        margin-bottom: 0.25em;
      }
      .mw-markdown ul ul,
      .mw-markdown ol ol,
      .mw-markdown ul ol,
      .mw-markdown ol ul {
        margin-bottom: 0;
        margin-top: 0.25em;
      }

      /* Bold & italic */
      .mw-markdown strong {
        font-weight: 600;
        color: #111827;
      }
      .mw-markdown em {
        font-style: italic;
      }

      /* Inline code */
      .mw-markdown code {
        background-color: #f3f4f6;
        padding: 0.15em 0.35em;
        border-radius: 0.25em;
        font-size: 0.88em;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      }

      /* Code blocks */
      .mw-markdown pre {
        background-color: #1f2937;
        color: #e5e7eb;
        padding: 0.875em 1em;
        border-radius: 0.5em;
        overflow-x: auto;
        margin: 0.75em 0;
        font-size: 0.85em;
      }
      .mw-markdown pre code {
        background-color: transparent;
        padding: 0;
        color: inherit;
      }

      /* Tables */
      .mw-markdown table {
        width: 100%;
        border-collapse: collapse;
        margin: 0.75em 0;
        font-size: 0.95em;
      }
      .mw-markdown th,
      .mw-markdown td {
        border: 1px solid #e5e7eb;
        padding: 0.4em 0.75em;
        text-align: left;
      }
      .mw-markdown th {
        background-color: #f9fafb;
        font-weight: 600;
      }

      /* Blockquotes */
      .mw-markdown blockquote {
        border-left: 3px solid #e5e7eb;
        padding-left: 1em;
        margin: 0.75em 0;
        color: #6b7280;
      }

      /* Links */
      .mw-markdown a {
        color: #2563eb;
        text-decoration: underline;
      }

      /* Horizontal rules */
      .mw-markdown hr {
        border: none;
        border-top: 1px solid #e5e7eb;
        margin: 1.25em 0;
      }
    `}</style>
  )
}
